import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'

export type ConsentState = 'granted' | 'denied' | 'pending'

const CONSENT_KEY = 'pt_analytics_consent'

function getPostHog(): any {
  return typeof window !== 'undefined' ? (window as any).posthog : null
}

function readStoredConsent(): ConsentState | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(CONSENT_KEY)
    if (raw === 'granted' || raw === 'denied') return raw
  } catch {
    // ignore
  }
  return null
}

function writeStoredConsent(state: ConsentState) {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(CONSENT_KEY, state)
  } catch {
    // ignore
  }
}

function clearStoredConsent() {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(CONSENT_KEY)
  } catch {
    // ignore
  }
}

export function getConsent(): ConsentState {
  const stored = readStoredConsent()
  if (stored) return stored
  const posthog = getPostHog()
  if (!posthog) return 'pending'
  const status = posthog.get_explicit_consent_status?.()
  if (status === 'granted' || status === 'denied' || status === 'pending') return status
  return 'pending'
}

export function setConsent(state: ConsentState) {
  const posthog = getPostHog()
  if (!posthog) return
  if (state === 'granted') {
    posthog.opt_in_capturing()
  } else if (state === 'denied') {
    posthog.opt_out_capturing()
  }
  writeStoredConsent(state)
  window.dispatchEvent(new Event('pt_consent_changed'))
}

export function resetConsent() {
  const posthog = getPostHog()
  if (!posthog) return
  posthog.clear_opt_in_out_capturing?.()
  clearStoredConsent()
  window.location.reload()
}

export function ConsentBanner() {
  const [consent, setConsentState] = useState<ConsentState>(() => getConsent())

  useEffect(() => {
    const resolved = getConsent()
    setConsentState(resolved)
    const posthog = getPostHog()
    if (posthog && resolved !== 'pending') {
      if (resolved === 'granted') {
        posthog.opt_in_capturing()
      } else {
        posthog.opt_out_capturing()
      }
    }
    const onChange = () => setConsentState(getConsent())
    window.addEventListener('pt_consent_changed', onChange)
    return () => window.removeEventListener('pt_consent_changed', onChange)
  }, [])

  if (consent !== 'pending') return null

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-4 py-3">
      <div className="mx-auto flex max-w-6xl flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Share anonymous usage analytics. Change this in Settings.
        </p>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="ghost" size="sm" onClick={() => { setConsent('denied'); setConsentState('denied') }}>
            Decline
          </Button>
          <Button size="sm" onClick={() => { setConsent('granted'); setConsentState('granted') }}>
            Allow
          </Button>
        </div>
      </div>
    </div>
  )
}
